import React, { Suspense, useMemo, useState } from 'react';

import { DownloadOutlined, EyeOutlined, PrinterOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import classNames from 'classnames';
import { useDispatch } from 'dva';
import _ from 'lodash';
import { useIntl } from 'umi';

import FileTypeIcon from '@/pages/applications/application-detail/tab-documents/FileTypeIcon';
import { resolveJsonDataCreatorAvatar } from '@/utils/core/data-transformation/jsonDataCreatorHelpers';
import { fileContentTypeClassification, fileSizeDisplay } from '@/utils/infrastructure/file-system';
import UserAvatar from '../UserAvatar';
import styles from './style.less';

// Lazy: FileRow is imported by DVA models (e.g. Entities). Eager DetailsDrawer
// pulls @/utils → getIntl() before umi is ready.
const FileDetailsDrawer = React.lazy(() => import('@/components/Common/FileDetailsDrawer'));

const FileRow = ({
  fileName,
  downloadUrl,
  documentTemporaryUrl,
  contentType,
  isRecent,
  isReadable,
  isBase64,
  size,
  dense,
  ...props
}) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [detailsFile, setDetailsFile] = useState(null);

  const displayName =
    typeof fileName === 'string' ? fileName : fileName != null ? String(fileName) : '';
  const classification =
    props.fileTypeClassification ?? fileContentTypeClassification[contentType] ?? 0;

  const handleDownload = () => {
    dispatch({
      type: 'global/directDownload',
      payload: {
        fileName: displayName,
        downloadUrl: downloadUrl ?? documentTemporaryUrl,
      },
    });
  };

  const handlePreview = () => {
    setDetailsFile({
      id: props.id,
      fileName: displayName,
      downloadUrl,
      documentTemporaryUrl,
      previewUrl: props.previewUrl,
      contentType,
      content: props.content,
      isBase64,
      size,
      creator: props.creator,
      created: props.created,
      updated: props.updated,
      fileTypeClassification: classification,
      processInstanceId: props.processInstanceId,
      processName: props.processName,
      processInstanceName: props.processInstanceName,
      source: props.source,
      formName: props.formName,
      fieldName: props.fieldName,
      actionName: props.actionName,
    });
  };

  const previewLabel = formatMessage({ id: 'global.text.preview' });
  const downloadLabel = formatMessage({ id: 'global.button.download' });
  const printLabel = formatMessage({ id: 'global.button.print' });

  // Same owner resolution as document manager (GridView / ListView / columns).
  const owner = useMemo(
    () => (isReadable && props.creator ? resolveJsonDataCreatorAvatar(props.creator) : null),
    [isReadable, props.creator],
  );
  const hasOwner = Boolean(owner?.name) && !dense;
  const sizeLabel = size != null && size > 0 ? fileSizeDisplay(+size) : null;
  const trailing = props.children;
  // Dense edit (with remove trailing) stays one-line; dense view keeps preview/download.
  const showFileActions = !isRecent && !(dense && trailing);

  return (
    <>
      <div
        className={classNames(styles.fileRow, {
          [styles.fileRowEditable]: !isReadable,
          [styles.fileRowCompact]: !isReadable || dense,
          [styles.fileRowDense]: dense,
        })}
      >
        <FileTypeIcon
          fileTypeClassification={classification}
          fileName={displayName}
          size={dense ? 'xs' : isReadable ? 'md' : 'sm'}
        />

        <div className={classNames(styles.meta, dense && styles.metaDense)}>
          <span className={classNames(styles.name, dense && styles.nameDense)} title={displayName}>
            {displayName}
          </span>

          {(hasOwner || sizeLabel) && (
            <div className={classNames(styles.subMeta, dense && styles.subMetaDense)}>
              {hasOwner && (
                <span className={styles.creator}>
                  <UserAvatar
                    name={owner.name}
                    id={owner.id}
                    isAgent={owner.isAgent}
                    size={16}
                    truncate
                  />
                </span>
              )}
              {hasOwner && sizeLabel && !dense && <span className={styles.metaDot} aria-hidden />}
              {sizeLabel && (
                <span className={classNames(styles.size, dense && styles.sizeDense)}>
                  {sizeLabel}
                </span>
              )}
            </div>
          )}
        </div>

        {(showFileActions || trailing) && (
          <div className={styles.actions}>
            {showFileActions && (
              <>
                <Tooltip title={previewLabel}>
                  <Button
                    type="text"
                    size="small"
                    className={classNames(styles.actionBtn, dense && styles.actionBtnDense)}
                    icon={<EyeOutlined />}
                    onClick={handlePreview}
                    aria-label={previewLabel}
                  />
                </Tooltip>
                <Tooltip title={downloadLabel}>
                  <Button
                    type="text"
                    size="small"
                    className={classNames(styles.actionBtn, dense && styles.actionBtnDense)}
                    icon={<DownloadOutlined />}
                    onClick={handleDownload}
                    aria-label={downloadLabel}
                  />
                </Tooltip>
                {props.print ? (
                  <Tooltip title={printLabel}>
                    <Button
                      type="text"
                      size="small"
                      className={classNames(styles.actionBtn, dense && styles.actionBtnDense)}
                      icon={<PrinterOutlined />}
                      onClick={_.debounce(() => {
                        window.open(
                          `https://drive.google.com/viewerng/viewer?url=${
                            downloadUrl ?? documentTemporaryUrl
                          }`,
                        );
                      }, 500)}
                      aria-label={printLabel}
                    />
                  </Tooltip>
                ) : null}
              </>
            )}
            {trailing}
          </div>
        )}
      </div>

      {detailsFile ? (
        <Suspense fallback={null}>
          <FileDetailsDrawer file={detailsFile} onClose={() => setDetailsFile(null)} />
        </Suspense>
      ) : null}
    </>
  );
};

FileRow.defaultProps = {
  isRecent: false,
  isReadable: true,
  dense: false,
};

export default FileRow;
